import { EditPageRoute } from 'constants/common';
import { ROUTER_CARBON_ACCOUNT_MANAGEMENT_PROJECT_LOAN, ROUTER_IDENTIFY_PROJECT_COMPANY, ROUTER_ROOT } from 'constants/router';
import { CaretDownOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { SettingOutlinedIcon, UserOutlinedIcon } from '@miotech/prism';
import { Popover, Select, Tooltip } from 'antd';
import useRedux from 'hooks/useRedux';
import { get, keys } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EFeature } from 'services/user-role/types';
import { cursorPointer, singleLineEllipsis } from 'styles/template-style';
import { setLocalStorage, USER_ROLE } from 'utils/storage';
import { checkMenusPermission } from 'utils/utils';
import { getResourceListByRoleId } from 'services/user-role/user.service';

const UserRolePermission = () => {
  const {
    selector: {
      userInfo,
      userCurrentRole
    },
    dispatch
  } = useRedux(s => s.userRole);
  const location = useLocation();
  const history = useHistory();

  const hasManagerPermission = useMemo(() => {
    const builtInRoleList = userInfo?.builtInRoleList || [];
    return builtInRoleList.includes(EFeature.SysAdmin) || builtInRoleList.includes(EFeature.OrgAdmin);
  }, [userInfo]);

  const findDepartmentNames = useCallback(() => {
    let departmentPathList: any[] = [];
    if (userInfo?.departmentPathList && userInfo?.departmentPathList.length > 0) {
      departmentPathList = userInfo?.departmentPathList[0];
    }
    let current_name = '';
    let fullName = '';
    if (departmentPathList.length > 1) {
      current_name = departmentPathList[departmentPathList.length - 1].departmentName;
    } else if (departmentPathList.length === 1) {
      current_name = departmentPathList[0].departmentName;
    }
    fullName = departmentPathList.map(item => item.departmentName).join(' - ');
    return {
      departmentName: current_name,
      fullName
    };
  }, [userInfo]);
  const handleChangeUserPermission = useCallback(async (value?: string) => {
    const selectedRole = (userInfo?.customRoleList || []).find(item => item.id === value);
    if (selectedRole) {
      const resourceData = await getResourceListByRoleId(value || '');
      const menus_list = (resourceData?.result || []).filter(item => Boolean(item.selected));
      await dispatch.userRole.updateResourceListCache(menus_list);
      await dispatch.userRole.updateUserCurrentRoleCache(selectedRole);
      setLocalStorage(USER_ROLE, selectedRole.id || '');
      const editKeys = keys(EditPageRoute);
      let targetpath = location.pathname;
      if (targetpath.startsWith('/gvi')) {
        targetpath = targetpath.substring(4);
      }
      for (let i = 0; i < editKeys.length; i++) {
        if (location.pathname.includes(editKeys[i])) {
          targetpath = EditPageRoute[editKeys[i]];
          break;
        }
      }
      if (location.pathname.includes(ROUTER_IDENTIFY_PROJECT_COMPANY) && Boolean((location.state as any || {}).pageType)) {
        targetpath = ROUTER_CARBON_ACCOUNT_MANAGEMENT_PROJECT_LOAN;
      }
      const hasPermission = checkMenusPermission(targetpath, menus_list);
      if (hasPermission) {
        if (targetpath) {
          return history.replace(targetpath);
        }
      } else {
        history.replace(ROUTER_ROOT);
      }
    }
  }, [userInfo, dispatch, history, location]);
  const contentRender = useMemo(() => {
    const department = findDepartmentNames();
    const rolelist = (userInfo?.customRoleList || []);
    return (
      <div className={styleContentRender}>
        <div className='item'>
          <div className='label'>归属组织</div>
          <div className={`name ${singleLineEllipsis}`}>
            <Tooltip
              title={department.fullName}
              overlayClassName={styleResetAntdTooltip}
              arrow={false}
            >
              {department.fullName}
            </Tooltip>
          </div>
        </div>
        {
          rolelist.length > 0 && <div className='item'>
            <div className='label'>业务角色</div>
            <div className='value'>
              <Select
                defaultValue={userCurrentRole?.id || ''}
                size='small'
                style={{ maxWidth: 250, minWidth: 150, border: 'none' }}
                onChange={handleChangeUserPermission}
                options={rolelist}
                fieldNames={{
                  label: 'roleName',
                  value: 'id'
                }}
                suffixIcon={<CaretDownOutlined />}
              />
            </div>
          </div>
        }
      </div >
    );
  }, [userInfo, findDepartmentNames, handleChangeUserPermission, userCurrentRole]);

  const onPermissionClick = useCallback(() => {
    const current_host = get(window, '__config__.host');
    const builtInRoleList = userInfo.builtInRoleList || [];
    let current_path = '';
    if (builtInRoleList.includes(EFeature.SysAdmin)) {
      current_path = '/quota-management';
    } else if (builtInRoleList.includes(EFeature.OrgAdmin)) {
      current_path = '/user-management';
    }
    window.location.href = `${current_host}/gvi-admin${current_path}`;
  }, [userInfo]);
  return (
    <div className={styleUserRole}>
      {
        hasManagerPermission && <div className="permissionbtn" onClick={onPermissionClick}>
          <SettingOutlinedIcon />
          <span>权限管理</span>
        </div>
      }
      <Popover
        arrow={false}
        align={{
          offset: [10, 0],
        }}
        rootClassName={styleRootPopover}
        placement="bottomRight"
        content={contentRender}
      >
        <div className='user-icon'>
          <UserOutlinedIcon className={cursorPointer} />
        </div>
      </Popover>
    </div>
  );
};

export default memo(UserRolePermission);

const styleUserRole = css`
  display: flex;
  padding: 6px 0 6px 17px;
  align-items: center;
  color: #e7e7e7;
  font-size: 14px;
  gap: 17px;
  .permissionbtn{
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }
  .user-icon{
    display: flex;
    width: 28px;
    height: 28px;
    justify-content: center;
    align-items: center;
    border-radius: 100px;
    background: var(--fourth, #2D3446);
  }
`;
const styleRootPopover = css`
  z-index: 999;
`;
const styleContentRender = css`
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px; /* 157.143% */
  max-width: 400px;
  .item{
    display: flex;
    gap: 8px;
    max-width: 400px;
    text-align: justify;
    /* cn_regular/regular_14 */
    font-family: "PingFang SC";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px; /* 157.143% */
    .label{
      color: #959A9F;
    }
    .name{
      flex: 1;
      color: #262A2F;
    }
  }
  .ant-select-selector{
    border:none !important;
    box-shadow: none !important;
    padding-left:0 !important;
  }
`;

export const styleResetAntdTooltip = css`
  max-width: 400px;
  .ant-tooltip-inner {
    background-color: #fff;
    color: #262a2f;
    padding: 8px 12px;
    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    font-size: 12px;
    white-space: pre-wrap;
  }
`;
